import React, { useEffect, useState, useCallback } from 'react';
import { FiPlus, FiArrowLeft, FiPlay, FiCheck, FiBookOpen, FiTrash2, FiFileText, FiDownload, FiClock } from 'react-icons/fi';
import {
  PayRun, PayRunLine, listRuns, createRun, getRun, calculateRun, approveRun, postRunToGL, deleteRun,
  generateStubs, stubPdfUrl, eftCsvUrl, runTimesheetHours, FREQUENCIES,
} from './payrollApi';
import { setFormDirty, registerSaveHandler, confirmDiscardChanges } from '../../lib/unsavedChanges';

const money = (n: any) => `$${Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function RunPayrollTab() {
  const [runs, setRuns] = useState<PayRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<Partial<PayRun>>({});
  const [openId, setOpenId] = useState<number | null>(null);
  const [detail, setDetail] = useState<{ run: PayRun; lines: PayRunLine[] } | null>(null);
  const [busy, setBusy] = useState('');
  const [stub, setStub] = useState<PayRunLine | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setRuns(await listRuns()); }
    catch (e: any) { setError(e.message || 'Could not load runs'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openRun = useCallback(async (id: number) => {
    setError(''); setOpenId(id); setStub(null);
    try { const d = await getRun(id); setDetail({ run: d.run, lines: d.lines }); }
    catch (e: any) { setError(e.message || 'Could not open run'); }
  }, []);

  // --- Create draft run ---
  const saveDraft = useCallback(async (): Promise<boolean> => {
    if (!draft.period_start || !draft.period_end || !draft.pay_date) { setError('Period start, end and pay date are required.'); return false; }
    setBusy('create'); setError('');
    try {
      const r = await createRun({ ...draft, pay_frequency: draft.pay_frequency || 'biweekly' });
      setFormDirty(false); setCreating(false); setDraft({});
      await load(); await openRun(r.pay_run_id);
      return true;
    } catch (e: any) { setError(e.message || 'Could not create run'); return false; }
    finally { setBusy(''); }
  }, [draft, load, openRun]);
  useEffect(() => { if (creating) { registerSaveHandler(saveDraft); return () => registerSaveHandler(null); } }, [creating, saveDraft]);

  const startCreate = () => {
    setCreating(true); setFormDirty(false);
    setDraft({ pay_frequency: 'biweekly', pay_date: todayISO(), period_start: '', period_end: '', tax_year: new Date().getFullYear() });
  };
  const cancelCreate = async () => { if (await confirmDiscardChanges()) { setFormDirty(false); setCreating(false); setDraft({}); } };

  // --- Actions on an open run ---
  const doCalc = async (fromTimesheets = false) => {
    if (!detail) return;
    setBusy('calc'); setError('');
    try {
      let lines;
      if (fromTimesheets) {
        lines = await runTimesheetHours(detail.run.pay_run_id);
        if (!lines.length) { setError('No timesheets found for this run\'s period. Enter hours in the Timesheets tab first.'); setBusy(''); return; }
      }
      await calculateRun(detail.run.pay_run_id, lines);
      await openRun(detail.run.pay_run_id); await load();
    }
    catch (e: any) { setError(e.message || 'Calculation failed'); }
    finally { setBusy(''); }
  };
  const doApprove = async () => {
    if (!detail) return;
    if (!window.confirm('Approve this run? This locks the amounts and updates each employee\'s year-to-date totals.')) return;
    setBusy('approve'); setError('');
    try { await approveRun(detail.run.pay_run_id); await openRun(detail.run.pay_run_id); await load(); }
    catch (e: any) { setError(e.message || 'Approval failed'); }
    finally { setBusy(''); }
  };
  const doPost = async () => {
    if (!detail) return;
    if (!window.confirm('Post this payroll to the General Ledger? A balanced journal entry will be created.')) return;
    setBusy('post'); setError('');
    try { const r = await postRunToGL(detail.run.pay_run_id); await openRun(detail.run.pay_run_id); await load(); alert(`Posted to GL — journal #${r.journal_id}`); }
    catch (e: any) { setError(e.message || 'Posting failed'); }
    finally { setBusy(''); }
  };
  const removeRun = async (r: PayRun) => {
    if (!window.confirm('Delete this draft run?')) return;
    try { await deleteRun(r.pay_run_id); await load(); }
    catch (e: any) { setError(e.message || 'Could not delete'); }
  };
  const doGenStubs = async () => {
    if (!detail) return;
    setBusy('stubs'); setError('');
    try { const r = await generateStubs(detail.run.pay_run_id); await openRun(detail.run.pay_run_id); alert(`Generated ${r.generated} pay stub${r.generated === 1 ? '' : 's'} — filed into DMS (employees with a linked login see them in their own Documents).`); }
    catch (e: any) { setError(e.message || 'Could not generate stubs'); }
    finally { setBusy(''); }
  };

  const closeDetail = () => { setOpenId(null); setDetail(null); setStub(null); };

  // ---------------- Create form ----------------
  if (creating) {
    return (
      <div className="pay-card">
        <div className="pay-toolbar"><button className="pay-btn ghost sm" onClick={cancelCreate}><FiArrowLeft /> Back</button>
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-lg)' }}>New pay run</h2></div>
        {error && <div className="pay-error">{error}</div>}
        <div className="pay-grid">
          <div className="pay-field"><label>Pay frequency</label>
            <select value={draft.pay_frequency} onChange={(e) => { setDraft({ ...draft, pay_frequency: e.target.value }); setFormDirty(true); }}>
              {FREQUENCIES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </div>
          <div className="pay-field"><label>Period start</label><input type="date" value={draft.period_start || ''} onChange={(e) => { setDraft({ ...draft, period_start: e.target.value }); setFormDirty(true); }} /></div>
          <div className="pay-field"><label>Period end</label><input type="date" value={draft.period_end || ''} onChange={(e) => { setDraft({ ...draft, period_end: e.target.value }); setFormDirty(true); }} /></div>
          <div className="pay-field"><label>Pay date</label><input type="date" value={draft.pay_date || ''} onChange={(e) => { setDraft({ ...draft, pay_date: e.target.value, tax_year: new Date(e.target.value).getFullYear() }); setFormDirty(true); }} /></div>
        </div>
        <div className="pay-note" style={{ marginTop: 'var(--spacing-md)' }}>
          Calculating will include every <strong>active</strong> employee on this pay frequency, using their saved hours/salary.
        </div>
        <div className="pay-toolbar" style={{ marginTop: 'var(--spacing-lg)', marginBottom: 0 }}>
          <button className="pay-btn" onClick={saveDraft} disabled={busy === 'create'}>{busy === 'create' ? 'Creating…' : 'Create run'}</button>
          <button className="pay-btn ghost" onClick={cancelCreate}>Cancel</button>
        </div>
      </div>
    );
  }

  // ---------------- Run detail ----------------
  if (openId && detail) {
    const r = detail.run;
    const canCalc = r.status === 'draft' || r.status === 'calculated';
    const canApprove = r.status === 'calculated';
    const canPost = r.status === 'approved';
    return (
      <div>
        <div className="pay-toolbar">
          <button className="pay-btn ghost sm" onClick={closeDetail}><FiArrowLeft /> All runs</button>
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-lg)' }}>Run #{r.pay_run_id}</h2>
          <span className={`pay-badge ${r.status}`}>{r.status}</span>
          <span className="spacer" />
          {canCalc && <button className="pay-btn" onClick={() => doCalc(false)} disabled={!!busy}><FiPlay /> {busy === 'calc' ? 'Calculating…' : 'Calculate'}</button>}
          {canCalc && <button className="pay-btn ghost" onClick={() => doCalc(true)} disabled={!!busy} title="Use hours entered in the Timesheets tab for this period"><FiClock /> From timesheets</button>}
          {canApprove && <button className="pay-btn" onClick={doApprove} disabled={!!busy}><FiCheck /> Approve</button>}
          {canPost && <button className="pay-btn" onClick={doPost} disabled={!!busy}><FiBookOpen /> Post to GL</button>}
          {detail.lines.length > 0 && r.status !== 'draft' &&
            <button className="pay-btn ghost" onClick={doGenStubs} disabled={!!busy}><FiFileText /> {busy === 'stubs' ? 'Generating…' : 'Generate stubs'}</button>}
          {detail.lines.length > 0 && (r.status === 'approved' || r.status === 'posted') &&
            <a className="pay-btn ghost" href={eftCsvUrl(r.pay_run_id)} target="_blank" rel="noreferrer"><FiDownload /> EFT file</a>}
        </div>
        {error && <div className="pay-error">{error}</div>}
        <div className="pay-sub" style={{ marginBottom: 'var(--spacing-md)' }}>
          {r.period_start?.slice(0, 10)} → {r.period_end?.slice(0, 10)} · pay date {r.pay_date?.slice(0, 10)} · {FREQUENCIES.find((f) => f.value === r.pay_frequency)?.label}
          {r.gl_journal_id ? ` · GL journal #${r.gl_journal_id}` : ''}
        </div>

        <div className="pay-stats">
          <div className="pay-stat"><div className="label">Employees</div><div className="value">{r.employee_count}</div></div>
          <div className="pay-stat"><div className="label">Gross</div><div className="value">{money(r.total_gross)}</div></div>
          <div className="pay-stat"><div className="label">Deductions</div><div className="value">{money(r.total_deductions)}</div></div>
          <div className="pay-stat"><div className="label">Net pay</div><div className="value">{money(r.total_net)}</div></div>
          <div className="pay-stat"><div className="label">Employer cost</div><div className="value">{money(r.total_employer_cost)}</div></div>
        </div>

        {stub ? <StubView line={stub} run={r} onClose={() => setStub(null)} />
          : detail.lines.length === 0 ? <div className="pay-empty">No lines yet — click <strong>Calculate</strong> to compute pay for all active employees.</div>
          : (
          <div className="pay-card" style={{ padding: 0, overflowX: 'auto' }}>
            <table className="pay-table">
              <thead><tr>
                <th>Employee</th><th className="num">Gross</th><th className="num">CPP</th><th className="num">CPP2</th><th className="num">EI</th>
                <th className="num">Fed tax</th><th className="num">Prov tax</th><th className="num">Net</th><th></th>
              </tr></thead>
              <tbody>
                {detail.lines.map((l) => (
                  <tr key={l.pay_run_line_id}>
                    <td>{l.first_name} {l.last_name}</td>
                    <td className="num">{money(l.gross_pay)}</td>
                    <td className="num">{money(l.cpp_employee)}</td>
                    <td className="num">{money(l.cpp2_employee)}</td>
                    <td className="num">{money(l.ei_employee)}</td>
                    <td className="num">{money(l.federal_tax)}</td>
                    <td className="num">{money(l.provincial_tax)}</td>
                    <td className="num pos">{money(l.net_pay)}</td>
                    <td className="num" style={{ whiteSpace: 'nowrap' }}>
                      <button className="pay-btn ghost sm" onClick={() => setStub(l)}><FiFileText /> View</button>{' '}
                      {l.paystub_document_id
                        ? <a className="pay-btn ghost sm" href={stubPdfUrl(l.pay_run_line_id)} target="_blank" rel="noreferrer"><FiDownload /> PDF</a>
                        : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // ---------------- Runs list ----------------
  return (
    <div>
      <div className="pay-toolbar">
        <button className="pay-btn" onClick={startCreate}><FiPlus /> New pay run</button>
        <span className="spacer" />
        <span style={{ color: 'var(--secondary)', fontSize: 'var(--font-size-sm)' }}>{runs.length} run{runs.length === 1 ? '' : 's'}</span>
      </div>
      {error && <div className="pay-error">{error}</div>}
      <div className="pay-card" style={{ padding: 0, overflowX: 'auto' }}>
        {loading ? <div className="pay-empty">Loading…</div>
          : runs.length === 0 ? <div className="pay-empty">No pay runs yet.</div>
          : (
          <table className="pay-table">
            <thead><tr>
              <th>Run</th><th>Period</th><th>Pay date</th><th>Freq</th><th className="num">Emps</th>
              <th className="num">Gross</th><th className="num">Net</th><th>Status</th><th></th>
            </tr></thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.pay_run_id} className="pay-row-clickable" onClick={() => openRun(r.pay_run_id)}>
                  <td>#{r.pay_run_id}</td>
                  <td>{r.period_start?.slice(0, 10)} → {r.period_end?.slice(0, 10)}</td>
                  <td>{r.pay_date?.slice(0, 10)}</td>
                  <td>{r.pay_frequency}</td>
                  <td className="num">{r.employee_count}</td>
                  <td className="num">{money(r.total_gross)}</td>
                  <td className="num pos">{money(r.total_net)}</td>
                  <td><span className={`pay-badge ${r.status}`}>{r.status}</span></td>
                  <td className="num" onClick={(e) => e.stopPropagation()}>
                    {(r.status === 'draft' || r.status === 'calculated') &&
                      <button className="pay-btn ghost sm" onClick={() => removeRun(r)}><FiTrash2 /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function StubView({ line, run, onClose }: { line: PayRunLine; run: PayRun; onClose: () => void }) {
  const c = line.calc_detail || {};
  const row = (label: string, val: any, cls = '') => (
    <div className={`stub-line ${cls}`}><span>{label}</span><span>{money(val)}</span></div>
  );
  return (
    <div>
      <div className="pay-toolbar">
        <button className="pay-btn ghost sm" onClick={onClose}><FiArrowLeft /> Back to run</button>
        {line.paystub_document_id &&
          <a className="pay-btn ghost sm" href={stubPdfUrl(line.pay_run_line_id)} target="_blank" rel="noreferrer"><FiDownload /> Download PDF</a>}
      </div>
      <div className="pay-stub">
        <h3>{line.first_name} {line.last_name}</h3>
        <div className="pay-sub">Pay date {run.pay_date?.slice(0, 10)} · {run.period_start?.slice(0, 10)} → {run.period_end?.slice(0, 10)}</div>
        <div style={{ marginTop: 'var(--spacing-lg)' }}>
          <strong>Earnings</strong>
          {row('Gross pay', line.gross_pay, 'total')}
        </div>
        <div style={{ marginTop: 'var(--spacing-md)' }}>
          <strong>Deductions</strong>
          {row('CPP', line.cpp_employee)}
          {Number(line.cpp2_employee) > 0 && row('CPP2 (additional)', line.cpp2_employee)}
          {row('EI', line.ei_employee)}
          {row('Federal income tax', line.federal_tax)}
          {row('Provincial income tax', line.provincial_tax)}
          {Number(line.other_deductions) > 0 && row('Other deductions', line.other_deductions)}
          {row('Total deductions', line.total_deductions, 'total')}
        </div>
        <div style={{ marginTop: 'var(--spacing-md)' }}>
          {row('NET PAY', line.net_pay, 'total')}
        </div>
        <div style={{ marginTop: 'var(--spacing-md)', fontSize: 'var(--font-size-xs)', color: 'var(--secondary)' }}>
          Employer contributions: CPP {money(line.cpp_employer)} · CPP2 {money(line.cpp2_employer)} · EI {money(line.ei_employer)}
          {line.vacation_accrued > 0 ? ` · Vacation accrued ${money(line.vacation_accrued)}` : ''}
          {c.annualizedTaxableIncome ? ` · Annualized taxable ${money(c.annualizedTaxableIncome)}` : ''}
        </div>
      </div>
    </div>
  );
}
